import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import toast from "../../utils/toast";
import {
  fetchTestOrders,
  fetchTestOrderResults,
  updateTestOrderStatus,
} from "../../store/slices/testOrderSlice";
import { fetchTests } from "../../store/slices/testSlice";
import { fetchTestParameters } from "../../store/slices/testParameterSlice";
import { fetchReferenceRanges } from "../../store/slices/referenceRangeSlice";
import { fetchDynamicQuestions } from "../../store/slices/dynamicQuestionSlice";
import ResultEntryForm from "./ResultEntryForm";

const PrinterIcon = ({ className = "h-4 w-4" }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M7 8V4h10v4"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M6 18H5a3 3 0 0 1-3-3v-5a3 3 0 0 1 3-3h14a3 3 0 0 1 3 3v5a3 3 0 0 1-3 3h-1"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M7 14h10v6H7v-6Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M17 11h.01"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
    />
  </svg>
);

const statusGroups = {
  pending: ["ordered", "sample_collected"],
  in_progress: ["in_progress"],
  completed: ["completed"],
};

const TestWorkbench = () => {
  const dispatch = useDispatch();
  const { testOrders, loading, error } = useSelector((s) => s.testOrders || {});
  const { tests } = useSelector((s) => s.tests || {});
  const { parameters: allParameters } = useSelector(
    (s) => s.testParameters || {}
  );
  // NOTE: referenceRangeSlice stores ranges under `ranges`, not `referenceRanges`.
  const { ranges: referenceRanges } = useSelector((s) => s.referenceRanges || {});
  const { dynamicQuestions } = useSelector((s) => s.dynamicQuestions || {});

  const [activeTab, setActiveTab] = useState("pending");
  const [search, setSearch] = useState("");
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [showEntry, setShowEntry] = useState(false);
  const [expandedVisitIds, setExpandedVisitIds] = useState({});
  const [criticalByOrderId, setCriticalByOrderId] = useState({});
  const [activeOrderId, setActiveOrderId] = useState(null);
  const orderCardRefs = useRef({});
  const formApiByOrderIdRef = useRef({});
  const [actionStateByOrderId, setActionStateByOrderId] = useState({});

  // Fetch all required data
  useEffect(() => {
    dispatch(fetchTestOrders({}));
    dispatch(fetchTests());
    dispatch(fetchTestParameters());
    dispatch(fetchReferenceRanges());
    dispatch(fetchDynamicQuestions());
  }, [dispatch]);

  const normalizeDynamicAnswers = useCallback((value) => {
    if (value === null || value === undefined) return {};
    if (typeof value === "object") return value;
    if (typeof value === "string") {
      const s = value.trim();
      if (!s) return {};
      try {
        const parsed = JSON.parse(s);
        return parsed && typeof parsed === "object" ? parsed : {};
      } catch {
        return {};
      }
    }
    return {};
  }, []);

  const normalizeOrder = useCallback((o) => {
    if (!o) return o;
    const age = o.age ?? o.patient_age ?? null;
    const gender = o.gender ?? o.patient_gender ?? null;
    const dynamicAnswers = normalizeDynamicAnswers(o.dynamic_answers);
    return {
      ...o,
      age,
      gender,
      dynamic_answers: dynamicAnswers,
    };
  }, [normalizeDynamicAnswers]);

  const orders = useMemo(() => {
    const list = Array.isArray(testOrders) ? testOrders : testOrders?.data || [];
    return list
      .map(normalizeOrder)
      .filter((o) => String(o?.test_testing_mode || "").toLowerCase() !== "morphology");
  }, [testOrders, normalizeOrder]);

  const testsById = useMemo(() => {
    const list = Array.isArray(tests) ? tests : tests?.data || [];
    return Object.fromEntries(list.map((t) => [t.id, t]));
  }, [tests]);

  const normalizeStatus = useCallback((s) => (s ? String(s) : ""), []);

  const summarizeGroupStatus = useCallback((ordersInGroup) => {
    const statuses = ordersInGroup.map((o) => normalizeStatus(o.status));
    if (statuses.some((s) => s === "in_progress")) return "in_progress";
    if (statuses.some((s) => s === "sample_collected")) return "sample_collected";
    if (statuses.length > 0 && statuses.every((s) => s === "completed")) return "completed";
    if (statuses.some((s) => s === "ordered")) return "ordered";
    if (statuses.some((s) => s === "cancelled")) return "cancelled";
    return statuses.find(Boolean) || "unknown";
  }, [normalizeStatus]);

  const summarizeGroupPriority = useCallback((ordersInGroup) => {
    if (ordersInGroup.some((o) => String(o.priority || "").toLowerCase() === "urgent")) {
      return "urgent";
    }
    return "normal";
  }, []);

  const visitGroups = useMemo(() => {
    const map = new Map();

    const toYmd = (value) => {
      if (!value) return null;
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return null;
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    };

    for (const o of orders) {
      const visitId = o.visit_id ?? o.visitId;
      const key = visitId == null ? `order-${o.id}` : `visit-${visitId}`;
      const existing = map.get(key);

      const patient =
        o.patient_name || o.full_name || `Patient #${o.patient_id || "-"}`;
      const card = o.card_number || "";
      const visitDate = o.visit_date || toYmd(o.ordered_at) || null;
      const visitTime = o.visit_time || null;

      if (!existing) {
        map.set(key, {
          key,
          visit_id: visitId ?? null,
          visit_date: visitDate,
          visit_time: visitTime,
          patient_name: patient,
          card_number: card,
          age: o.age ?? null,
          gender: o.gender ?? null,
          orders: [o],
        });
      } else {
        existing.orders.push(o);
        if (!existing.visit_date && visitDate) existing.visit_date = visitDate;
        if (!existing.visit_time && visitTime) existing.visit_time = visitTime;
        if (!existing.patient_name && patient) existing.patient_name = patient;
        if (!existing.card_number && card) existing.card_number = card;
        if (existing.age == null && o.age != null) existing.age = o.age;
        if (existing.gender == null && o.gender != null) existing.gender = o.gender;
      }
    }

    const out = Array.from(map.values());
    for (const g of out) {
      g.status = summarizeGroupStatus(g.orders);
      g.priority = summarizeGroupPriority(g.orders);
      g.test_names = g.orders.map((o) => testsById[o.test_id]?.name || `Test #${o.test_id}`);
      g.tracking_code = g.visit_id ? `WB-${g.visit_id}` : `WB-${g.orders?.[0]?.id}`;
    }

    out.sort((a, b) => {
      const ad = a.visit_date || "";
      const bd = b.visit_date || "";
      if (ad !== bd) return bd.localeCompare(ad);
      const at = a.visit_time || "";
      const bt = b.visit_time || "";
      return bt.localeCompare(at);
    });

    return out;
  }, [orders, testsById, summarizeGroupPriority, summarizeGroupStatus]);

  // Build complete parameter data with reference ranges
  const parametersByTestId = useMemo(() => {
    const list = Array.isArray(allParameters) ? allParameters : [];
    const ranges = Array.isArray(referenceRanges) ? referenceRanges : [];
    const questions = Array.isArray(dynamicQuestions) ? dynamicQuestions : [];

    const grouped = {};

    for (const p of list) {
      const testId = Number(p.test_id);
      if (!testId) continue;

      if (!grouped[testId]) grouped[testId] = [];

      // Find reference ranges for this parameter
      const paramRanges = ranges.filter(
        (range) => Number(range?.parameter_id) === Number(p?.id)
      );

      // Find dynamic questions for this test
      const testQuestions = questions.filter((q) => q.test_id === testId);

      grouped[testId].push({
        id: p.id,
        test_id: testId,
        test_sample_type_id: p.test_sample_type_id ?? null,
        name: p.parameter_name,
        unit: p.unit,
        data_type: p.data_type || "numeric",
        reference_ranges: paramRanges, // Multiple ranges based on conditions
        dynamic_questions: testQuestions,
        description: p.description,
        created_at: p.created_at,
      });
    }
    return grouped;
  }, [allParameters, referenceRanges, dynamicQuestions]);

  const getPatientAgeInYears = (order) => {
    if (!order) return null;

    // Prefer precise age based on date_of_birth when available
    const dob = order.patient_date_of_birth || order.date_of_birth;
    if (dob) {
      const birthDate = new Date(dob);
      const now = new Date();
      if (!Number.isNaN(birthDate.getTime()) && now >= birthDate) {
        const diffMs = now.getTime() - birthDate.getTime();
        const years = diffMs / (1000 * 60 * 60 * 24 * 365.25);
        return years;
      }
    }

    const raw = order.age ?? order.patient_age;
    if (raw === null || raw === undefined) return null;
    const n = Number(raw);
    return Number.isNaN(n) ? null : n;
  };

  const formatPatientAgeLabel = (order) => {
    if (!order) return "N/A";

    const dob = order.patient_date_of_birth || order.date_of_birth;
    if (dob) {
      const birth = new Date(dob);
      const today = new Date();
      if (!Number.isNaN(birth.getTime()) && today >= birth) {
        let years = today.getFullYear() - birth.getFullYear();
        let months = today.getMonth() - birth.getMonth();
        let days = today.getDate() - birth.getDate();

        if (days < 0) {
          months -= 1;
          const prevMonth = new Date(today.getFullYear(), today.getMonth(), 0);
          days += prevMonth.getDate();
        }
        if (months < 0) {
          years -= 1;
          months += 12;
        }

        if (years > 0) {
          if (years < 2 && months > 0) return `${years}y ${months}m`;
          return `${years}y`;
        }
        if (months > 0) {
          if (days > 0) return `${months}m ${days}d`;
          return `${months}m`;
        }
        return `${days}d`;
      }
    }

    const a = order.age ?? order.patient_age;
    if (a === null || a === undefined || a === "") return "N/A";
    return `${a}y`;
  };

  const normalizeRangeAgeToYears = (value, unit) => {
    if (value === null || value === undefined) return null;
    const n = Number(value);
    if (!Number.isFinite(n)) return null;
    const u = String(unit || "years").toLowerCase();
    if (u === "years") return n;
    if (u === "months") return n / 12;
    if (u === "weeks") return n / 52.1429;
    if (u === "days") return n / 365.25;
    return n;
  };

  const ageMatchesRange = (patientAgeYears, range) => {
    if (patientAgeYears === null || patientAgeYears === undefined) return true;
    const minYears = normalizeRangeAgeToYears(range.min_age, range.age_unit);
    const maxYears = normalizeRangeAgeToYears(range.max_age, range.age_unit);

    if (minYears !== null && patientAgeYears < minYears) return false;
    if (maxYears !== null && patientAgeYears > maxYears) return false;
    return true;
  };

  // Find appropriate reference range for a parameter based on patient data
  const findReferenceRangeWithReason = (parameter, order) => {
    if (
      !parameter.reference_ranges ||
      parameter.reference_ranges.length === 0
    ) {
      return { range: null, reason: "No reference ranges defined" };
    }

    const patientAgeYears = getPatientAgeInYears(order);
    const patientGender = order?.gender ?? null;
    const testSampleTypeId = order?.test_sample_type_id ?? null;
    const sampleType = order?.sample_type; // From test order

    // Pregnancy status (best-effort)
    let pregnancyStatus = "both";
    const answers = order?.dynamic_answers;
    if (answers && typeof answers === "object") {
      const asText = JSON.stringify(answers).toLowerCase();
      if (asText.includes("pregnan")) {
        pregnancyStatus = asText.includes("not") ? "not_pregnant" : "pregnant";
      }
    }

    const allRanges = parameter.reference_ranges;

    // Step 1: exact match (test_sample_type + gender + age + pregnancy + sample_type when present)
    const exact = allRanges.find(
      (r) =>
        (!r.test_sample_type_id || !testSampleTypeId || Number(r.test_sample_type_id) === Number(testSampleTypeId)) &&
        (r.gender === "both" || r.gender === patientGender) &&
        (r.pregnancy_status === "both" || r.pregnancy_status === pregnancyStatus) &&
        (!r.sample_type || !sampleType || r.sample_type === sampleType) &&
        ageMatchesRange(patientAgeYears, r)
    );
    if (exact) return { range: exact, reason: "Exact match (test-sample-type + gender + age)" };

    // Step 2: test-sample-type match (most important), then gender+age
    const byTst = testSampleTypeId
      ? allRanges.filter(
          (r) =>
            !r.test_sample_type_id || Number(r.test_sample_type_id) === Number(testSampleTypeId)
        )
      : allRanges;

    const genderAge = byTst.find(
      (r) =>
        (r.gender === "both" || r.gender === patientGender) &&
        (r.pregnancy_status === "both" || r.pregnancy_status === pregnancyStatus) &&
        ageMatchesRange(patientAgeYears, r)
    );
    if (genderAge) {
      return {
        range: genderAge,
        reason: testSampleTypeId
          ? "Fallback: test-sample-type match + gender/age"
          : "Fallback: gender/age match",
      };
    }

    // Step 3: age-only match (still prefer test-sample-type filtered set)
    const ageOnly = byTst.find((r) => ageMatchesRange(patientAgeYears, r));
    if (ageOnly) {
      return {
        range: ageOnly,
        reason: testSampleTypeId ? "Fallback: test-sample-type + age-only" : "Fallback: age-only",
      };
    }

    // Step 4: default
    return { range: allRanges[0], reason: "Default reference range" };
  };

  const findReferenceRange = (parameter, order) =>
    findReferenceRangeWithReason(parameter, order).range;

  const filteredGroups = useMemo(() => {
    const set = new Set(statusGroups[activeTab] || []);
    const s = search.trim().toLowerCase();

    return visitGroups.filter((g) => {
      const statusOk = set.size === 0 ? true : set.has(g.status);
      if (!statusOk) return false;
      if (!s) return true;

      const testText = (g.test_names || []).join(" ").toLowerCase();
      const patient = (g.patient_name || "").toLowerCase();
      const card = (g.card_number || "").toLowerCase();
      const visit = String(g.visit_id || "");
      const tracking = String(g.tracking_code || "").toLowerCase();
      const orderIds = (g.orders || []).map((o) => String(o.id)).join(" ");

      return (
        testText.includes(s) ||
        patient.includes(s) ||
        card.includes(s) ||
        visit.includes(s) ||
        tracking.includes(s) ||
        orderIds.includes(s)
      );
    });
  }, [visitGroups, activeTab, search]);

  const counts = useMemo(() => {
    const pending = visitGroups.filter((g) => statusGroups.pending.includes(g.status)).length;
    const inProgress = visitGroups.filter((g) => statusGroups.in_progress.includes(g.status)).length;
    const completed = visitGroups.filter((g) => statusGroups.completed.includes(g.status)).length;
    const urgent = visitGroups.filter((g) => g.priority === "urgent").length;
    return { pending, inProgress, completed, urgent };
  }, [visitGroups]);

  const toggleExpanded = (key) => {
    setExpandedVisitIds((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handlePrintOrder = useCallback((order) => {
    const orderId = order?.id;
    if (!orderId) {
      toast.error("Missing order id");
      return;
    }

    const url = `/print-report?orderId=${encodeURIComponent(orderId)}`;
    const printWindow = window.open(
      url,
      "_blank",
      "width=1200,height=800,scrollbars=yes"
    );

    if (!printWindow) {
      // Popup likely blocked; fall back to same-tab navigation.
      window.location.href = url;
      return;
    }

    setTimeout(() => {
      try {
        printWindow.focus();
      } catch {
        // ignore
      }
    }, 250);
  }, []);

  const handlePrintGroup = useCallback(
    (group) => {
      const ordersInGroup = Array.isArray(group?.orders) ? group.orders : [];
      const firstCompleted = ordersInGroup.find(
        (o) => String(o?.status || "") === "completed"
      );
      const fallback = ordersInGroup[0] || null;
      const orderToPrint = firstCompleted || fallback;

      if (!orderToPrint) {
        toast.error("No orders to print for this visit");
        return;
      }

      handlePrintOrder(orderToPrint);
    },
    [handlePrintOrder]
  );

  const loadCriticalFlag = useCallback(
    async (order) => {
      const payload = await dispatch(fetchTestOrderResults(order.id)).catch(() => null);
      const data = payload?.payload?.data ?? payload?.payload ?? null;
      const parameterRows = Array.isArray(data?.parameter_results)
        ? data.parameter_results
        : [];
      const hasCritical = parameterRows.some((r) => r?.status === "critical");
      setCriticalByOrderId((prev) => ({ ...prev, [order.id]: hasCritical }));
    },
    [dispatch]
  );

  const eligibleForEntry = useCallback((order) => {
    const s = String(order?.status || "");
    return s === "sample_collected" || s === "in_progress" || s === "completed";
  }, []);

  const toMillis = useCallback((value) => {
    if (!value) return null;
    const ms = Date.parse(value);
    return Number.isNaN(ms) ? null : ms;
  }, []);

  const getGroupMillis = useCallback(
    (g) => {
      const date = g?.visit_date;
      const time = g?.visit_time;
      if (date && time) return toMillis(`${date}T${time}`);
      if (date) return toMillis(date);
      return null;
    },
    [toMillis]
  );

  const getOrderMillis = useCallback(
    (o, g) => {
      const direct = toMillis(o?.ordered_at) ?? toMillis(o?.created_at);
      if (direct != null) return direct;
      return getGroupMillis(g);
    },
    [getGroupMillis, toMillis]
  );

  const entryQueue = useMemo(() => {
    const groupsSorted = [...visitGroups].sort((a, b) => {
      const am = getGroupMillis(a);
      const bm = getGroupMillis(b);
      const ax = am == null ? Number.POSITIVE_INFINITY : am;
      const bx = bm == null ? Number.POSITIVE_INFINITY : bm;
      return ax - bx; // oldest first
    });

    const items = [];
    for (const g of groupsSorted) {
      const ordersSorted = [...(g.orders || [])].sort((a, b) => {
        const am = getOrderMillis(a, g);
        const bm = getOrderMillis(b, g);
        const ax = am == null ? Number.POSITIVE_INFINITY : am;
        const bx = bm == null ? Number.POSITIVE_INFINITY : bm;
        if (ax !== bx) return ax - bx;
        return Number(a?.id || 0) - Number(b?.id || 0);
      });

      for (const o of ordersSorted) {
        if (!eligibleForEntry(o)) continue;
        items.push({
          groupKey: g.key,
          group: g,
          orderId: o.id,
          order: o,
          ms: getOrderMillis(o, g) ?? getGroupMillis(g) ?? 0,
        });
      }
    }
    return items;
  }, [eligibleForEntry, getGroupMillis, getOrderMillis, visitGroups]);

  const findNextEntryAfter = useCallback(
    ({ groupKey, orderId }) => {
      const idx = entryQueue.findIndex(
        (x) => String(x.groupKey) === String(groupKey) && String(x.orderId) === String(orderId)
      );
      if (idx < 0) return entryQueue[0] || null;
      return entryQueue[idx + 1] || null;
    },
    [entryQueue]
  );

  const openEntryTarget = useCallback(
    async (target) => {
      if (!target) {
        setSelectedGroup(null);
        setActiveOrderId(null);
        setShowEntry(true);
        return;
      }
      setSelectedGroup(target.group);
      const list = Array.isArray(target?.group?.orders) ? target.group.orders : [];
      await Promise.all(list.map((o) => loadCriticalFlag(o)));
      setActiveOrderId(target.orderId);
      setShowEntry(true);
    },
    [loadCriticalFlag]
  );

  const openVisitEntry = async (group, preferredOrderId = null) => {
    setSelectedGroup(group);
    // Preload critical flags for the visit (best effort)
    const list = Array.isArray(group?.orders) ? group.orders : [];
    await Promise.all(list.map((o) => loadCriticalFlag(o)));
    // Default selection: the next eligible order in this visit (by time)
    const sorted = [...list].sort((a, b) => {
      const am = getOrderMillis(a, group);
      const bm = getOrderMillis(b, group);
      const ax = am == null ? Number.POSITIVE_INFINITY : am;
      const bx = bm == null ? Number.POSITIVE_INFINITY : bm;
      if (ax !== bx) return ax - bx;
      return Number(a?.id || 0) - Number(b?.id || 0);
    });

    const nextInVisit = sorted.find((o) => eligibleForEntry(o)) || sorted[0] || null;
    setActiveOrderId(nextInVisit?.id ?? null);
    setShowEntry(true);
  };

  const closeEntry = () => {
    setSelectedGroup(null);
    setShowEntry(false);
    setActiveOrderId(null);
  };

  const groupSignature = useCallback((g) => {
    const list = Array.isArray(g?.orders) ? g.orders : [];
    return `${g?.key || ""}|${g?.status || ""}|${list
      .map((o) => `${o?.id}:${o?.status}`)
      .join(";")}`;
  }, []);

  // Keep the open modal synced when the store refreshes (e.g., after finalize)
  useEffect(() => {
    if (!selectedGroup?.key) return;
    const updated = visitGroups.find((g) => g.key === selectedGroup.key);
    if (!updated) return;
    if (groupSignature(updated) !== groupSignature(selectedGroup)) {
      setSelectedGroup(updated);
    }
  }, [visitGroups, selectedGroup, groupSignature]);

  const handleOrderSaved = useCallback(
    async (order, info) => {
      await loadCriticalFlag(order);
      // Draft save does not change order status, so avoid refetching the whole list.
      // Refetch only when finalizing (status becomes completed).
      if (info?.finalized) {
        await dispatch(fetchTestOrders({}));
        const next = findNextEntryAfter({
          groupKey: selectedGroup?.key,
          orderId: order?.id,
        });
        await openEntryTarget(next);
      }
    },
    [dispatch, findNextEntryAfter, loadCriticalFlag, openEntryTarget, selectedGroup?.key]
  );

  const updateActionState = useCallback((next) => {
    const orderId = next?.order_id;
    if (!orderId) return;
    setActionStateByOrderId((prev) => ({ ...prev, [orderId]: next }));
  }, []);

  // When active order changes, scroll it into view (modal)
  useEffect(() => {
    if (!showEntry) return;
    if (!activeOrderId) return;
    const el = orderCardRefs.current?.[activeOrderId];
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [activeOrderId, showEntry]);

  const canEnterResultsForGroup = useCallback((group) => {
    const list = Array.isArray(group?.orders) ? group.orders : [];
    return list.some((o) =>
      ["sample_collected", "in_progress", "completed"].includes(String(o?.status || ""))
    );
  }, []);

  const startVisit = async (group) => {
    const list = Array.isArray(group?.orders) ? group.orders : [];
    const ordered = list.filter((o) => String(o?.status || "") === "ordered");
    if (ordered.length === 0) return;

    try {
      setActionStateByOrderId((prev) => ({ ...prev, [group.key]: { busy: true } }));
      for (const o of ordered) {
        // sequential to avoid stampeding API
        // eslint-disable-next-line no-await-in-loop
        await dispatch(updateTestOrderStatus({ id: o.id, status: "in_progress" })).unwrap();
      }
      await dispatch(fetchTestOrders({}));
      const updated = visitGroups.find((g) => g.key === group.key) || group;
      await openVisitEntry(updated);
    } catch (e) {
      toast.error(e?.message || "Failed to start visit");
    } finally {
      setActionStateByOrderId((prev) => {
        const next = { ...prev };
        delete next[group.key];
        return next;
      });
    }
  };

  const markInProgress = async (order) => {
    try {
      await dispatch(
        updateTestOrderStatus({ id: order.id, status: "in_progress" })
      ).unwrap();
    } catch (e) {
      console.error("Failed to mark in progress:", e);
    }
  };

  const markCompleted = async (order) => {
    try {
      await dispatch(
        updateTestOrderStatus({ id: order.id, status: "completed" })
      ).unwrap();
    } catch (e) {
      console.error("Failed to mark completed:", e);
    }
  };

  const getTestParameters = (order) => {
    const all = parametersByTestId[order.test_id] || [];
    const tstId = order?.test_sample_type_id ?? null;
    let params = all;

    // Some environments have legacy/mismatched sample-type wiring (e.g. parameters
    // linked to a sample_type_id while orders carry a test_sample_type_id, or vice-versa).
    // If filtering would hide all parameters, fall back to showing all parameters
    // for the test so laboratorists can enter all results.
    if (tstId) {
      const filtered = all.filter(
        (p) =>
          p.test_sample_type_id == null ||
          Number(p.test_sample_type_id) === Number(tstId)
      );
      params = filtered.length > 0 ? filtered : all;
    }

    // Enhance parameters with their reference ranges for this specific patient
    const enhancedParams = params.map((param) => {
      const picked = findReferenceRangeWithReason(param, order);
      return {
        ...param,
        applicable_reference_range: picked.range,
        applicable_reference_range_reason: picked.reason,
      };
    });

    return enhancedParams;
  };

  const hasDynamicQuestions = (order) => {
    const params = parametersByTestId[order.test_id] || [];
    return (
      params.some(
        (param) => param.dynamic_questions && param.dynamic_questions.length > 0
      ) ||
      (order?.dynamic_answers && Object.keys(order.dynamic_answers).length > 0)
    );
  };

  const getDynamicQuestionsInfo = (order) => {
    const params = parametersByTestId[order.test_id] || [];
    const allQuestions = params.flatMap(
      (param) => param.dynamic_questions || []
    );

    return {
      questions: allQuestions,
      answers: order?.dynamic_answers || {},
    };
  };

  const columns = [
    { key: "visit", name: "Visit" },
    { key: "patient", name: "Patient" },
    { key: "tests", name: "Tests" },
    { key: "priority", name: "Priority" },
    { key: "status", name: "Status" },
    { key: "actions", name: "Actions" },
  ];

  // Show loading state
  if (loading && orders.length === 0) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex justify-center items-center h-64">
          <div className="text-lg text-gray-600">Loading test orders...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-[#235F72]">
          Laboratory Workbench
        </h1>
        <div className="flex gap-3">
          <button
            onClick={() => {
              dispatch(fetchTestOrders({}));
              dispatch(fetchTestParameters());
              dispatch(fetchReferenceRanges());
              dispatch(fetchDynamicQuestions());
            }}
            className="px-4 py-2 bg-[#235F72] text-white rounded-lg hover:bg-[#1a4a5a]"
          >
            {loading ? "Refreshing..." : "Refresh All"}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
          {String(error)}
        </div>
      )}

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow-md border-l-4 border-blue-500">
          <div className="text-2xl font-bold text-blue-600">
            {counts.pending}
          </div>
          <div className="text-sm text-gray-600">Pending Visits</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-md border-l-4 border-orange-500">
          <div className="text-2xl font-bold text-orange-600">
            {counts.inProgress}
          </div>
          <div className="text-sm text-gray-600">In Progress Visits</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-md border-l-4 border-green-500">
          <div className="text-2xl font-bold text-green-600">
            {counts.completed}
          </div>
          <div className="text-sm text-gray-600">Completed Visits</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-md border-l-4 border-red-500">
          <div className="text-2xl font-bold text-red-600">
            {counts.urgent}
          </div>
          <div className="text-sm text-gray-600">Urgent Priority</div>
        </div>
      </div>

      {/* Tabs + Search */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          <div className="relative w-full lg:max-w-md">
            <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              🔍
            </div>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by patient, card, visit, order ID, test…"
              className="w-full pl-10 pr-10 py-2.5 rounded-xl border border-gray-200 bg-white text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#36F1A2] focus:border-transparent"
            />
            {search?.trim() ? (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-lg text-gray-500 hover:bg-gray-50"
                aria-label="Clear search"
                title="Clear"
              >
                ✕
              </button>
            ) : null}
          </div>

          <div className="text-xs text-gray-500 lg:text-right">
            Workflow: <span className="font-semibold text-gray-700">sample collected</span> →{" "}
            <span className="font-semibold text-gray-700">in progress</span> →{" "}
            <span className="font-semibold text-gray-700">completed</span>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {[
            {
              id: "pending",
              label: "Pending",
              count: counts.pending,
            },
            {
              id: "in_progress",
              label: "In Progress",
              count: counts.inProgress,
            },
            {
              id: "completed",
              label: "Completed",
              count: counts.completed,
            },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`h-10 px-4 rounded-xl flex items-center gap-2 text-sm font-semibold border shadow-sm whitespace-nowrap ${
                activeTab === t.id
                  ? "bg-[#235F72] text-white border-[#235F72]"
                  : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
              }`}
            >
              {t.label}
              <span
                className={`px-2 py-0.5 text-xs rounded-full ${
                  activeTab === t.id
                    ? "bg-white/90 text-[#235F72]"
                    : "bg-gray-100 text-gray-700"
                }`}
              >
                {t.count}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-gray-200">
                {columns.map((c) => (
                  <th
                    key={c.key}
                    className="text-left py-4 px-6 text-[#235F72] font-semibold"
                  >
                    {c.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredGroups.map((g) => {
                const expanded = !!expandedVisitIds[g.key];

                const priorityBadge =
                  g.priority === "urgent"
                    ? "bg-red-100 text-red-700 border border-red-200"
                    : "bg-gray-100 text-gray-800";

                const groupHasCritical = (g.orders || []).some(
                  (o) => criticalByOrderId[o.id]
                );

                const statusMap = {
                  ordered: "bg-blue-100 text-blue-800 border border-blue-200",
                  sample_collected:
                    "bg-purple-100 text-purple-800 border border-purple-200",
                  in_progress:
                    "bg-orange-100 text-orange-800 border border-orange-200",
                  completed:
                    "bg-green-100 text-green-800 border border-green-200",
                  cancelled: "bg-gray-100 text-gray-700 border border-gray-200",
                };

                const card = g.card_number ? `(${g.card_number})` : "";
                const orderedAt = g.visit_date
                  ? `${g.visit_date}${g.visit_time ? ` • ${g.visit_time}` : ""}`
                  : "-";

                return (
                  <React.Fragment key={g.key}>
                    <tr className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-4 px-6 font-semibold text-[#235F72]">
                        <button
                          onClick={() => toggleExpanded(g.key)}
                          className="text-left hover:underline"
                        >
                          {g.visit_id ? `Visit #${g.visit_id}` : "Visit"}
                        </button>
                        <div className="text-xs text-gray-500 mt-1">
                          Tracking: {g.tracking_code}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          Ordered: {orderedAt}
                        </div>
                      </td>

                      <td className="py-4 px-6">
                        <div className="text-gray-800 font-medium">
                          {g.patient_name}
                          {card && (
                            <span className="text-gray-500 ml-1">{card}</span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          Age: {g.age ?? "N/A"} • Gender: {g.gender ?? "N/A"}
                        </div>
                      </td>

                      <td className="py-4 px-6">
                        <div className="text-sm text-gray-800">
                          {g.test_names?.length ? `${g.test_names.length} test(s)` : "-"}
                        </div>
                        {g.test_names?.length ? (
                          <div className="text-xs text-gray-500 mt-1">
                            {g.test_names.slice(0, 3).join(", ")}
                            {g.test_names.length > 3 ? "…" : ""}
                          </div>
                        ) : null}
                      </td>

                      <td className="py-4 px-6">
                        <div className="flex items-center gap-2">
                          <span
                            className={`px-2 py-1 text-xs rounded-full ${priorityBadge}`}
                          >
                            {g.priority}
                          </span>
                          {groupHasCritical && (
                            <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-700 border border-red-200">
                              Critical Result
                            </span>
                          )}
                        </div>
                      </td>

                      <td className="py-4 px-6">
                        <span
                          className={`inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-full border ${
                            statusMap[g.status] || ""
                          }`}
                        >
                          {g.status ? g.status.replace(/_/g, " ") : "unknown"}
                        </span>
                      </td>

                      <td className="py-4 px-6">
                        <div className="flex items-center gap-2 flex-nowrap">
                          {String(g.status || "") === "completed" ? (
                            <button
                              type="button"
                              onClick={() => handlePrintGroup(g)}
                              className="text-sm bg-white text-gray-700 px-4 py-2 rounded-xl border border-gray-200 hover:bg-gray-50 font-semibold shadow-sm whitespace-nowrap"
                            >
                              <span className="inline-flex items-center gap-1">
                                <PrinterIcon className="h-4 w-4" />
                                Print
                              </span>
                            </button>
                          ) : null}

                          {String(g.status || "") === "ordered" && !canEnterResultsForGroup(g) ? (
                            <button
                              type="button"
                              onClick={() => startVisit(g)}
                              className="text-sm bg-[#085DB6] text-white px-4 py-2 rounded-xl hover:bg-[#074a9b] font-semibold shadow-sm whitespace-nowrap"
                            >
                              Start
                            </button>
                          ) : null}

                          <button
                            onClick={() => openVisitEntry(g)}
                            disabled={!canEnterResultsForGroup(g)}
                            className={
                              canEnterResultsForGroup(g)
                                ? "text-sm bg-[#085DB6] text-white px-4 py-2 rounded-xl hover:bg-[#074a9b] font-semibold shadow-sm whitespace-nowrap"
                                : "text-sm bg-gray-100 text-gray-400 px-4 py-2 rounded-xl font-semibold cursor-not-allowed border border-gray-200 whitespace-nowrap"
                            }
                          >
                            Enter Results
                          </button>
                        </div>
                      </td>
                    </tr>

                    {expanded ? (
                      <tr className="border-b border-gray-100">
                        <td colSpan={columns.length} className="px-6 pb-5">
                          <div className="mt-3 rounded-lg bg-gray-50 border border-gray-200 p-3">
                            <div className="text-sm font-semibold text-gray-700 mb-2">
                              Tests in this visit
                            </div>
                            <div className="overflow-x-auto">
                              <table className="w-full">
                                <thead>
                                  <tr className="text-left text-xs font-semibold text-gray-600 border-b border-gray-200">
                                    <th className="py-2 pr-4">Order</th>
                                    <th className="py-2 pr-4">Test</th>
                                    <th className="py-2 pr-4">Status</th>
                                    <th className="py-2 pr-4">Sample</th>
                                    <th className="py-2 pr-4">Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(g.orders || []).map((o) => {
                                    const testName =
                                      testsById[o.test_id]?.name || `Test #${o.test_id}`;

                                    const isUrgent =
                                      String(o.priority || "").toLowerCase() === "urgent";
                                    const isCritical = Boolean(criticalByOrderId[o.id]);

                                    return (
                                      <tr key={o.id} className="border-b border-gray-200 last:border-b-0">
                                        <td className="py-3 pr-4 font-semibold text-[#235F72]">
                                          #{o.id}
                                        </td>
                                        <td className="py-3 pr-4">
                                          <div className="font-medium text-gray-900">{testName}</div>
                                          <div className="mt-1 flex flex-wrap gap-2">
                                            {isUrgent && (
                                              <span className="inline-flex items-center px-2 py-0.5 bg-red-50 text-red-700 text-xs rounded border border-red-200">
                                                ⚡ Urgent
                                              </span>
                                            )}
                                            {isCritical && (
                                              <span className="inline-flex items-center px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded border border-red-200">
                                                🚨 Critical Result
                                              </span>
                                            )}
                                            {o.test_sample_type_id && (
                                              <span className="inline-flex items-center px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded border border-gray-200">
                                                TST #{o.test_sample_type_id}
                                              </span>
                                            )}
                                          </div>
                                          {hasDynamicQuestions(o) && (
                                            <div className="mt-1">
                                              <span className="inline-flex items-center px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded border border-blue-200">
                                                📋 Has Additional Info
                                              </span>
                                            </div>
                                          )}
                                        </td>
                                        <td className="py-3 pr-4">
                                          <span
                                            className={`inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-full border ${
                                              statusMap[o.status] || ""
                                            }`}
                                          >
                                            {o.status
                                              ? o.status.replace(/_/g, " ")
                                              : "unknown"}
                                          </span>
                                        </td>
                                        <td className="py-3 pr-4 text-sm text-gray-700">
                                          <div>{o.sample_type || "-"}</div>
                                          <div className="text-xs text-gray-500 mt-1">
                                            Sample Type ID: {o.sample_type_id ?? "-"}
                                          </div>
                                        </td>
                                        <td className="py-3 pr-4">
                                          <div className="flex items-center gap-2 flex-nowrap">
                                            {o.status !== "in_progress" &&
                                              o.status !== "completed" && (
                                                <button
                                                  onClick={() => markInProgress(o)}
                                                  className="text-sm bg-[#085DB6] text-white px-3 py-2 rounded-xl hover:bg-[#074a9b] font-semibold shadow-sm whitespace-nowrap"
                                                >
                                                  Start
                                                </button>
                                              )}
                                            {o.status === "in_progress" && (
                                              <button
                                                onClick={() => markCompleted(o)}
                                                className="text-sm bg-[#36F1A2] text-[#235F72] px-3 py-2 rounded-xl hover:bg-[#2dd191] font-semibold shadow-sm whitespace-nowrap"
                                              >
                                                Mark Completed
                                              </button>
                                            )}
                                            {o.status === "completed" && (
                                              <button
                                                onClick={() => handlePrintOrder(o)}
                                                className="text-sm bg-white text-gray-700 px-3 py-2 rounded-xl border border-gray-200 hover:bg-gray-50 font-semibold shadow-sm whitespace-nowrap"
                                              >
                                                <span className="inline-flex items-center gap-1">
                                                  <PrinterIcon className="h-4 w-4" />
                                                  Print
                                                </span>
                                              </button>
                                            )}
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>

                            <div className="mt-3 flex justify-end">
                              <button
                                onClick={() => openVisitEntry(g)}
                                disabled={!canEnterResultsForGroup(g)}
                                className="text-sm bg-[#235F72] text-white px-4 py-2 rounded-xl hover:bg-[#1a4a5a] font-semibold shadow-sm whitespace-nowrap"
                              >
                                Enter Results (Visit)
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredGroups.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            {loading ? "Loading..." : "No test orders found."}
          </div>
        )}
      </div>

      {/* Modal for result entry */}
      {showEntry && selectedGroup && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white w-full max-w-6xl rounded-xl shadow-lg max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-lg font-semibold text-[#235F72]">
                Results Entry
                {selectedGroup?.visit_id ? ` • Visit #${selectedGroup.visit_id}` : ""}
                {selectedGroup?.patient_name ? ` • ${selectedGroup.patient_name}` : ""}
              </h3>
              <button
                onClick={closeEntry}
                className="text-gray-500 hover:text-gray-700 text-xl"
              >
                ✕
              </button>
            </div>

            <div className="overflow-y-auto flex-1">
              <div className="px-6 pt-5">
                <div className="bg-gray-50 rounded-lg p-4 border">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
                    <div>
                      <div className="text-xs text-gray-500">Patient</div>
                      <div className="font-semibold text-gray-900">{selectedGroup?.patient_name || "-"}</div>
                      {selectedGroup?.card_number ? (
                        <div className="text-xs text-gray-600 mt-1">Card: {selectedGroup.card_number}</div>
                      ) : null}
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Visit</div>
                      <div className="font-semibold text-gray-900">
                        {selectedGroup?.visit_id ? `#${selectedGroup.visit_id}` : "-"}
                      </div>
                      <div className="text-xs text-gray-600 mt-1">
                        {selectedGroup?.visit_date || "-"}
                        {selectedGroup?.visit_time ? ` • ${selectedGroup.visit_time}` : ""}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Age / Gender</div>
                      <div className="font-semibold text-gray-900">
                        {formatPatientAgeLabel(selectedGroup)} / {(selectedGroup?.gender ?? "N/A")}
                      </div>
                      <div className="text-xs text-gray-600 mt-1">Tracking: {selectedGroup?.tracking_code || "-"}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Tests</div>
                      <div className="font-semibold text-gray-900">{selectedGroup?.test_names?.length || 0} test(s)</div>
                      <div className="text-xs text-gray-600 mt-1">
                        {(selectedGroup?.test_names || []).slice(0, 2).join(", ")}
                        {(selectedGroup?.test_names || []).length > 2 ? "…" : ""}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-6 pb-8">
                <div className="mt-5 space-y-6">
                  {(selectedGroup?.orders || []).map((o) => {
                    const testName = testsById[o.test_id]?.name || `Test #${o.test_id}`;
                    const sample = o.sample_type || "-";
                    const isCritical = Boolean(criticalByOrderId?.[o.id]);
                    const isActive = String(o.id) === String(activeOrderId);
                    const statusPill = (() => {
                      const statusMap = {
                        ordered: "bg-blue-100 text-blue-800 border border-blue-200",
                        sample_collected: "bg-purple-100 text-purple-800 border border-purple-200",
                        in_progress: "bg-orange-100 text-orange-800 border border-orange-200",
                        completed: "bg-green-100 text-green-800 border border-green-200",
                        cancelled: "bg-gray-100 text-gray-700 border border-gray-200",
                      };
                      return statusMap[o.status] || "bg-gray-100 text-gray-700 border border-gray-200";
                    })();

                    return (
                      <div
                        key={o.id}
                        ref={(el) => {
                          if (el) orderCardRefs.current[o.id] = el;
                        }}
                        onMouseDown={() => {
                          if (eligibleForEntry(o)) setActiveOrderId(o.id);
                        }}
                        onFocusCapture={() => {
                          if (eligibleForEntry(o)) setActiveOrderId(o.id);
                        }}
                        className={
                          "border rounded-xl bg-white shadow-sm overflow-hidden transition " +
                          (isActive
                            ? "ring-2 ring-[#36F1A2] ring-offset-2"
                            : "hover:shadow-md")
                        }
                      >
                        <div className="px-5 py-4 bg-gray-50 border-b flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                          <div>
                            <div className="text-xs text-gray-500">Order #{o.id}</div>
                            <div className="text-base font-semibold text-gray-900">
                              {testName}
                            </div>
                            <div className="mt-1 text-sm text-gray-600">
                              Sample: <span className="font-semibold">{sample}</span>
                              {o.test_sample_type_id ? (
                                <span className="ml-2 text-xs text-gray-600">TST #{o.test_sample_type_id}</span>
                              ) : null}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`px-2 py-1 text-xs rounded-full ${statusPill}`}>
                              {o.status ? o.status.replace(/_/g, " ") : "unknown"}
                            </span>
                            {isCritical ? (
                              <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-700 border border-red-200">
                                Critical result
                              </span>
                            ) : null}
                            {eligibleForEntry(o) ? (
                              <button
                                type="button"
                                onClick={() => setActiveOrderId(o.id)}
                                className={
                                  "px-3 py-1.5 text-xs font-semibold rounded-full border shadow-sm whitespace-nowrap " +
                                  (isActive
                                    ? "bg-[#36F1A2] text-[#235F72] border-transparent"
                                    : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50")
                                }
                              >
                                {isActive ? "Current" : "Go to"}
                              </button>
                            ) : null}
                          </div>
                        </div>

                        <div
                          className="px-2 md:px-4"
                          onFocusCapture={() => {
                            if (eligibleForEntry(o)) setActiveOrderId(o.id);
                          }}
                          onMouseDownCapture={() => {
                            if (eligibleForEntry(o)) setActiveOrderId(o.id);
                          }}
                        >
                          <ResultEntryForm
                            order={o}
                            onClose={() => {}}
                            autoCloseOnSave={false}
                            onSaved={(info) => handleOrderSaved(o, info)}
                            onActionStateChange={updateActionState}
                            showActions={false}
                            showPatientContext={false}
                            testParameters={getTestParameters(o)}
                            testDetails={testsById[o.test_id]}
                            dynamicQuestionsInfo={getDynamicQuestionsInfo(o)}
                            findReferenceRange={findReferenceRange}
                            ref={(api) => {
                              if (api) formApiByOrderIdRef.current[o.id] = api;
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Single shared action bar (controls the active order) */}
            <div className="border-t bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80">
              <div className="px-6 py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-[#235F72]">Actions</div>
                  <div className="mt-1 text-xs text-gray-500">
                    {activeOrderId ? (
                      <>Applying to <span className="font-semibold">Order #{activeOrderId}</span> (current)</>
                    ) : (
                      "Select an order to continue."
                    )}
                  </div>
                </div>

                {(() => {
                  const state = activeOrderId ? actionStateByOrderId?.[activeOrderId] : null;
                  const api = activeOrderId ? formApiByOrderIdRef.current?.[activeOrderId] : null;
                  const fallback = api?.getActionState ? api.getActionState() : null;

                  const isSaveDisabled = state?.isSaveDisabled ?? fallback?.isSaveDisabled ?? true;
                  const isFinalizeDisabled = state?.isFinalizeDisabled ?? fallback?.isFinalizeDisabled ?? true;
                  const isSubmitting = state?.isSubmitting ?? fallback?.isSubmitting ?? false;

                  return (
                    <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-end">
                      <button
                        type="button"
                        onClick={() => api?.saveDraft?.()}
                        disabled={!api || isSaveDisabled}
                        className={
                          "h-11 px-5 rounded-xl border text-sm font-semibold shadow-sm " +
                          (!api || isSaveDisabled
                            ? "border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed"
                            : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50")
                        }
                      >
                        {isSubmitting ? "Saving…" : "Save draft"}
                      </button>

                      <button
                        type="button"
                        onClick={() => api?.finalize?.()}
                        disabled={!api || isFinalizeDisabled}
                        className={
                          "h-11 px-6 rounded-xl text-sm font-semibold shadow-sm " +
                          (!api || isFinalizeDisabled
                            ? "bg-[#36F1A2]/30 text-[#235F72]/50 cursor-not-allowed"
                            : "bg-[#36F1A2] text-[#235F72] hover:bg-[#2dd191]")
                        }
                      >
                        {isSubmitting ? "Processing…" : "Verify & finalize"}
                      </button>

                      <button
                        type="button"
                        onClick={closeEntry}
                        className="h-11 px-5 rounded-xl bg-gray-700 text-white text-sm font-semibold shadow-sm hover:bg-gray-800"
                      >
                        Close
                      </button>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* If there are no pending/in-progress orders anywhere, show a "done" state */}
      {showEntry && !selectedGroup && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white w-full max-w-xl rounded-2xl shadow-lg overflow-hidden">
            <div className="p-6 border-b">
              <div className="text-lg font-semibold text-[#235F72]">Results Entry</div>
              <div className="mt-1 text-sm text-gray-600">
                No orders are currently pending or in progress.
              </div>
            </div>
            <div className="p-6">
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-800">
                <div className="font-semibold">You’re done.</div>
                <div className="text-sm mt-1">
                  There is no order to enter results for right now.
                </div>
              </div>
              <div className="mt-5 flex justify-end">
                <button
                  onClick={closeEntry}
                  className="px-4 py-2 bg-[#235F72] text-white rounded-xl hover:bg-[#1a4a5a] font-semibold shadow-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestWorkbench;
